import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from 'projects/patient/src/app/core/auth/services/config.service';
import { LocalStorageService } from 'projects/patient/src/app/core/auth/services/local-storage.service';
import { from, Observable, switchMap } from 'rxjs';
import {
  DoctorSlotResponse,
  PatientDetailsResponse,
  TrackingGoalRequest,
  UpdatePatientVitalsRequest,
} from '../appoinment-const/appoinment-model';
import { jsPDF } from 'jspdf';
import { Paging } from 'projects/patient/src/app/core/types/helper.model';

@Injectable({
  providedIn: 'root',
})
export class AppoimentService {
  http = inject(HttpClient);
  hostUrl = inject(ConfigService).getAPIUrl();
  patientHostUrl = inject(ConfigService).getPatientAPIUrl();
  localStorageService = inject(LocalStorageService);

  constructor() {}

  getPatientData(patientId: string): Observable<PatientDetailsResponse> {
    return this.http.get<PatientDetailsResponse>(`${this.hostUrl}/api/GetPatient/${patientId}`);
  }

  updateHeightWeight(patientId: string, payload: UpdatePatientVitalsRequest): Observable<any> {
    return this.http.put<any>(
      `${this.hostUrl}/api/Patient/UpdateHeightWeight/${patientId}`,
      payload,
    );
  }

  addPatientActivityTrackingGoal(patientId: string, payload: TrackingGoalRequest): Observable<any> {
    return this.http.post<any>(`${this.hostUrl}/api/PatientActivityTrackingGoal`, payload);
  }

  getAppoinment(): Observable<any> {
    return this.http.get<any>(`${this.hostUrl}/api/appoinmnet`);
  }

  getAppoinentSlot(doctorID: string, date: string): Observable<DoctorSlotResponse> {
    return this.http.get<DoctorSlotResponse>(
      `${this.hostUrl}/api/slot/doctorsavailableslotsbydate/${doctorID}/${date}`,
    );
  }

  getDoctorFee(paging?: any): Observable<any> {
    return this.http.get<any>(`${this.hostUrl}/api/doctorsFee`, { params: paging });
  }

  getPreAssessmentQuestions(patientid: string, followup: boolean): Observable<any> {
    return this.http.get<any>(`${this.hostUrl}/api/Questions/${patientid}?followup=${followup}`);
  }

  submitPreAssessment(payload: any): Observable<any> {
    // Replace with actual API endpoint
    return this.http.post<any>(`${this.hostUrl}/api/Answer`, payload);
  }

  getAssessmentScore(id: string, patientId: string): Observable<any> {
    return this.http.get<any>(
      `${this.hostUrl}/api/patient/${patientId}/score?pre_assessment_id=${id}`,
    );
  }

  createAppointment(paylaod: any): Observable<any> {
    return this.http.post<any>(`${this.hostUrl}/api/v2/appointment`, paylaod);
  }

  getPaymentId(payload: any): Observable<any> {
    return this.http.post<any>(`${this.hostUrl}/api/payment/CreateWebAppIntent`, payload);
  }

  uploadHealthReport(patientID: string, files: File[]): Observable<any> {
    return from(this.prepareFiles(files)).pipe(
      switchMap((convertedFiles: File[]) => {
        const formData = new FormData();

        convertedFiles.forEach((file) => {
          formData.append('report', file);
        });

        return this.http.post<any>(
          `${this.hostUrl}/api/uploadReport?patientId=${patientID}`,
          formData,
        );
      }),
    );
  }

  private async prepareFiles(files: File[]): Promise<File[]> {
    const result: File[] = [];

    for (const file of files) {
      if (file.type === 'image/png' || file.type === 'image/jpeg') {
        const pdfFile = await this.convertImageToPdf(file);
        result.push(pdfFile);
      } else {
        result.push(file);
      }
    }

    return result;
  }

  uploadBodyImages(front: File, left: File, right: File): Observable<any> {
    const formData = new FormData();
    formData.append('frontProfilePic', front);
    formData.append('leftProfilePic', left);
    formData.append('rightProfilePic', right);
    // Replace with actual API endpoint
    return this.http.post<any>(`${this.hostUrl}/api/uploadImages`, formData);
  }

  sortQuestions(questions: any[]): any[] {
    return questions
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .map((q) => ({
        ...q,
        questionOptions: (q.questionOptions || []).sort(
          (a: any, b: any) => (a.sequence || 0) - (b.sequence || 0),
        ),
      }));
  }

  private convertImageToPdf(imageFile: File): Promise<File> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const imgData = e.target.result;

        const pdf = new jsPDF();

        const img = new Image();
        img.src = imgData;

        img.onload = () => {
          const width = pdf.internal.pageSize.getWidth();
          const height = (img.height * width) / img.width;

          pdf.addImage(imgData, 'PNG', 0, 0, width, height);

          const pdfBlob = pdf.output('blob');

          const pdfFile = new File(
            [pdfBlob],
            imageFile.name.replace(/\.(png|jpg|jpeg)$/i, '.pdf'),
            { type: 'application/pdf' },
          );

          resolve(pdfFile);
        };
      };

      reader.readAsDataURL(imageFile);
    });
  }

  getAppointmentsByPatient(paging?: Paging): Observable<any> {
    let params = new HttpParams();
    if (paging) {
      for (const key in paging) {
        params = params.set(key, (paging as any)[key]);
      }
    }
    return this.http.get<any>(`${this.hostUrl}/api/appointments`, { params });
  }
}
